import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home, MessageSquare, MoreHorizontal, ChevronDown, Bell, X,
  LayoutDashboard, Calendar, Users, Building2, FileText,
  FolderOpen, FileSpreadsheet, Handshake, MessageSquareHeart, KeyRound,
  Inbox, MessageCircle, Mail, FolderArchive, Receipt, UserCog, ListTodo,
  ChevronsLeft, ChevronsRight, Palette, UserCheck, Sliders, CalendarClock, Send, ClipboardList,
  History, GitMerge, Archive, FileWarning, Repeat, FileEdit, Package,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";import { useUnreadMessages } from "../lib/unreadMessages";
import { useAttentionCounters } from "../lib/attentionCounters";
import { useHandoverPolling } from "../lib/handovers";
import { useQuoteDecisionPolling } from "../lib/quoteDecisionEvents";
import { useUnreadTeamQuotes } from "../lib/unreadTeamQuotes";
import { useUnreadUpdates } from "../lib/unreadUpdates";
import { useCustomerNotifications } from "../lib/customerNotifications";
import { useFeatureFlags } from "../lib/featureFlags";
import { MessageBadge } from "./MessageBadge";
import { AttentionBadge } from "./AttentionBadge";
import CustomerPickerModal from "./CustomerPickerModal";
import ImpersonationBanner from "./ImpersonationBanner";
import PinUnlockDialog from "./PinUnlockDialog";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

import { Brand, MobileMoreMenu, CustomerBottomNav, CustomerNavRow, TeamMobileNav, TeamSidebar } from "./LayoutChrome";
import OfflineBanner from "./OfflineBanner";
import SessionExpiryBanner from "./SessionExpiryBanner";
import InstallPrompt from "./InstallPrompt";
export default function Layout({ view }) {
  const {
    user, logout,
    effectiveRole, setEffectiveRole,
    impersonatedConcierge, setImpersonatedConcierge,
    activeConciergeId, activeConciergeName, isImpersonatingConcierge,
    uiPrefs, setUiPrefs,
  } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const location = useLocation();

  // Feb 2026 · 16 Sep 2026 · User-Directive „Beim Öffnen der App soll die
  // Concierge-App immer direkt auf der Home-Seite starten, nicht auf der
  // zuletzt besuchten Seite."
  //
  // Umsetzung: einmal pro Browser-Session (sessionStorage-Marker) auf
  // `/team` umleiten, sobald der User in der Concierge-Perspektive
  // mountet (echter Concierge ODER Admin-Impersonation eines Concierges).
  // Deep-Links innerhalb derselben Session bleiben unangetastet — nur
  // der aller-erste Layout-Mount pro Session leitet um. Ein Neustart des
  // Browsers ODER Öffnen eines neuen Tabs setzt den Marker zurück und
  // löst den Redirect neu aus.
  const conciergeHomeRedirectDone = useRef(false);
  const isTeam = view === "team";
  const isPartner = view === "partner";
  const isCustomer = !isTeam && !isPartner;
  useEffect(() => {
    if (conciergeHomeRedirectDone.current) return;
    conciergeHomeRedirectDone.current = true;
    if (!isTeam) return;
    const inConciergeView =
      user?.role === "concierge"
      || (user?.role === "admin" && effectiveRole === "concierge");
    if (!inConciergeView) return;
    try {
      const KEY = "weissblom.concierge.firstMountHandled";
      if (window.sessionStorage.getItem(KEY)) return;
      window.sessionStorage.setItem(KEY, "1");
      // Nur wenn wir NICHT bereits auf /team stehen (Home)
      if (location.pathname !== "/team") {
        nav("/team", { replace: true });
      }
    } catch { /* Private mode etc. — skip silently */ }
  }, [isTeam, user?.role, effectiveRole, location.pathname, nav]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [custMoreOpen, setCustMoreOpen] = useState(false); // Desktop „Mehr"-Dropdown (Kunden-Nav)
  // Feb 2026 · Kunden-Picker-Popup: öffnet sich, wenn ein Admin oder
  // Concierge im Header-Switcher „Kunden-Ansicht" wählt. Der aktuelle
  // Tab behält die Admin-Session; der gewählte Kunde wird in einem
  // neuen Tab per Impersonation-Link (2-min JWT) geöffnet.
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  // 20 Jun 2026 · Feature-Flag „objektbezogene Rechnungen" (admin-schaltbar).
  // Steuert die Sichtbarkeit der Rechnungs-Menüpunkte in Team- + Kundennav.
  const { propertyInvoices } = useFeatureFlags();

  const links = isPartner
    ? [
        { to: "/partner", label: "Aufträge", end: true, id: "nav-partner-assignments" },
      ]
    : isTeam
    ? [
        // `adminOnly` — Menüpunkt nur für Admin sichtbar.
        // `conciergeVisible` — auch für Concierge/Admin-Impersonation
        // sichtbar. Alle Menüpunkte OHNE dieses Flag verschwinden für
        // die Concierge-Perspektive (Feb 2026 · vereinfachte Ansicht).
        // Concierge sieht STRIKT NUR: Home · Kalender · Objekte ·
        // Projekte · Mein Profil.
        // Die `/team`-Route bleibt als ConciergeHome-Landing intakt —
        // erreichbar über den Weissblom-Logo-Klick oben links UND jetzt
        // zusätzlich als „Home"-Sidebar-Eintrag ganz oben.
        //
        // Feb 2026 · User-Directive „In der Concierge-Ansicht: Füge in der
        // linken Navigationsleiste ganz oben einen 'Home'-Button hinzu,
        // der zur Willkommens-/Startseite des Concierge zurückführt."
        // → Der Eintrag ist NUR in der Concierge-Perspektive sichtbar
        //   (`conciergeOnly`), damit Admins nicht plötzlich zwei
        //   Home-Einträge in ihrer Sidebar bekommen (Team-Übersicht ist
        //   ihre Startseite).
        { to: "/team", label: "Home", end: true, id: "nav-team-home", icon: Home, conciergeVisible: true, conciergeOnly: true },
        { to: "/team", label: "Team-Übersicht", end: true, id: "nav-team-dashboard", icon: LayoutDashboard },
        { to: "/team/calendar", label: "Kalender", id: "nav-team-calendar", icon: Calendar, conciergeVisible: true },
        { to: "/team/customers", label: "Kunden", id: "nav-team-customers", icon: Users },
        { to: "/team/properties", label: "Objekte", id: "nav-team-properties", icon: Building2, conciergeVisible: true },
        // Feb 2026 · User-Directive (5 Sep 2026): Deutsche Sprachversion
        // nennt die „Request Anything"-Sektion jetzt „Sonderwünsche".
        // Admin-Dashboard bleibt konstant deutsch (Team-Rolle sieht nur DE).
        { to: "/team/requests", label: "Sonderwünsche", id: "nav-team-requests", icon: Inbox },
        { to: "/team/bookings", label: "Buchungen", id: "nav-team-bookings", icon: CalendarClock, conciergeVisible: true },
        { to: "/team/updates", label: "Wochenberichte", id: "nav-team-updates", icon: FileText },
        { to: "/team/projects", label: "Projekte", id: "nav-team-projects", icon: FolderOpen, conciergeVisible: true },
        { to: "/team/quotes", label: "Angebote", id: "nav-team-quotes", icon: FileSpreadsheet },
        { to: "/team/service-packages", label: "Service Pakete", id: "nav-team-service-packages", icon: Package },
        { to: "/team/assignments", label: "Partner", id: "nav-team-assignments", icon: Handshake },
        { to: "/team/feedback", label: "Feedback", id: "nav-team-feedback", icon: MessageSquareHeart },
        { to: "/team/allowed", label: "Zugänge", id: "nav-team-allowed", icon: KeyRound },
        { to: "/team/settings/concierge-history", label: "Concierge-Historie", id: "nav-team-concierge-history", icon: History, adminOnly: true },
        { to: "/team/chat", label: "Nachrichten", id: "nav-team-chat", icon: MessageCircle, conciergeVisible: true },
        { to: "/team/emails", label: "E-Mails", id: "nav-team-emails", icon: Mail },
        { to: "/team/documents", label: "Dokumente", id: "nav-team-documents", icon: FolderArchive },
        // 20 Jun 2026 · Objektbezogene Rechnungen: standardmäßig aus (sevDesk-
        // Upload ist der aktive Weg). Ein Admin kann sie über den Feature-
        // Schalter (/team/settings/edit-pin) wieder freigeben — dann tauchen
        // „Rechnungen", „Rechnungsvorlage" und „Rechnungsarchiv" wieder auf.
        ...(propertyInvoices ? [
          { to: "/team/invoices", label: "Rechnungen", id: "nav-team-invoices", icon: Receipt },
          { to: "/team/settings/invoice-template", label: "Rechnungsvorlage", id: "nav-team-invoice-template", icon: FileEdit, adminOnly: true },
          { to: "/team/invoice-archive", label: "Rechnungsarchiv", id: "nav-team-invoice-archive", icon: Archive, financeAdminOnly: true },
        ] : []),
        // Feb 2026 · DSGVO Art. 17 · Deletion-Audit UI (5 Sep 2026):
        // §147 AO evidence trail one click away.
        { to: "/team/deletion-audit", label: "Löschungs-Audit", id: "nav-team-deletion-audit", icon: FileWarning, adminOnly: true },
        { to: "/team/profile", label: "Mein Profil", id: "nav-team-profile", icon: UserCog, conciergeVisible: true },
        { to: "/team/checklists", label: "Checklisten", id: "nav-team-checklists", icon: ListTodo },
        { to: "/team/impersonations", label: "Kundenansicht-Audit", id: "nav-team-impersonations", icon: UserCheck },
        { to: "/team/live-status-schema", label: "Live-Status-Struktur", id: "nav-team-live-status-schema", icon: Sliders, adminOnly: true },
        { to: "/team/recurring-tasks", label: "Wiederkehrend", id: "nav-team-recurring-tasks", icon: Repeat, conciergeVisible: true },
        { to: "/team/sevdesk", label: "Rechnungs-Upload", id: "nav-team-sevdesk", icon: Receipt, adminOnly: true },
        { to: "/team/settings/automated-messages", label: "Auto-Nachrichten", id: "nav-team-auto-msgs", icon: Send, adminOnly: true },
        { to: "/team/settings/checklist-master", label: "Checklisten-Vorlage", id: "nav-team-checklist-master", icon: ClipboardList, adminOnly: true },
        { to: "/team/settings/edit-pin", label: "Bearbeitungs-PIN", id: "nav-team-edit-pin", icon: KeyRound, adminOnly: true },
        { to: "/team/settings/pin-audit", label: "PIN-Audit-Log", id: "nav-team-pin-audit", icon: History, adminOnly: true },
        { to: "/team/settings/migration-review", label: "Migrations-Assistent", id: "nav-team-migration-review", icon: GitMerge, adminOnly: true },
        { to: "/team/settings/telegram", label: "Telegram-Benachrichtigung", id: "nav-team-telegram", icon: Send, adminOnly: true },
        { to: "/team/design-gallery", label: "Design-Guide", id: "nav-team-design-gallery", icon: Palette },
      ]
        .filter((l) => !l.adminOnly || effectiveRole === "admin")
        // Feb 2026 · DSGVO §147 AO Phase 7: Rechnungsarchiv nur fuer
        // Finance-Admins sichtbar.
        .filter((l) => !l.financeAdminOnly || !!user?.is_finance_admin)
        // Feb 2026 · Concierge-Ansicht: strikt reduziertes Menü. Trifft
        // sowohl echten Concierge-Login als auch Admin-Impersonation.
        .filter((l) => effectiveRole !== "concierge" || l.conciergeVisible)
        // Feb 2026 · `conciergeOnly` — der „Home"-Eintrag ganz oben ist
        // ausschließlich in der Concierge-Perspektive erwünscht. Admins
        // haben mit Team-Übersicht bereits ihren eigenen Startpunkt.
        .filter((l) => !l.conciergeOnly || effectiveRole === "concierge")
    : [
        // 28 Sep 2026 · User-Directive: „Mehr" enthält NUR noch Einstellungen,
        // Familie, Bewertungen, Support (in dieser Reihenfolge). Übersicht +
        // Nachrichten bleiben primär (untere Navi unverändert). Alle übrigen
        // Punkte leben nur noch als Startseiten-Kacheln.
        { to: "/app", label: t.nav.dashboard, end: true, id: "nav-cust-dashboard" },
        { to: "/app/chat", label: t.nav.chat, id: "nav-cust-chat" },
        { to: "/app/settings", label: t.nav.settings, id: "nav-cust-settings", inMore: true },
        { to: "/app/family", label: t.nav.family, id: "nav-cust-family", inMore: true },
        { to: "/app/ratings", label: lang === "de" ? "Bewertungen" : "Ratings", id: "nav-cust-ratings", inMore: true },
        { to: "/app/support", label: "Support", id: "nav-cust-support", inMore: true },
      ];

  // Feb 2026 · 3-Wege-Rollen-Switcher (Header-Segmented-Control).
  // -----------------------------------------------------------
  // Der Admin sieht drei Buttons — Admin · Concierge · Kunden-Ansicht.
  // Der Concierge sieht zwei — Concierge · Kunden-Ansicht (kann sich nicht
  // nach oben zu Admin klemmen, damit die UI-Vorschau ehrlich bleibt).
  // Kunde/Family/Partner sehen keinen Switcher.
  //
  // Klick auf „Kunden-Ansicht" navigiert nach `/app` (Kunden-Layout);
  // Klick auf Admin/Concierge navigiert nach `/team` und setzt die
  // effektive Rolle, damit Admin-only-UI-Elemente entsprechend ein-/
  // ausgeblendet werden. Die Wahl lebt nur im React-State (siehe
  // `AuthProvider`) und fällt beim Reload auf die echte Rolle zurück.
  // Feb 2026 · User-Directive: „Der Rollen-Switcher ist ausschließlich
  // für Accounts mit der Rolle 'admin' sichtbar. Concierge- und Kunden-
  // Accounts sehen ihn nicht." Deshalb liefern wir nur für Admins die
  // 3-Optionen-Liste — alle anderen bekommen eine leere Liste, was
  // `canSwitch = false` bewirkt und den Switcher komplett ausblendet.
  const viewOptions = useMemo(() => {
    if (user?.role === "admin") {
      return [
        { key: "admin",     label: "Admin",          short: "Adm" },
        { key: "concierge", label: "Concierge",      short: "Con" },
        { key: "customer",  label: "Kunden-Ansicht", short: "Kd"  },
      ];
    }
    return [];
  }, [user?.role]);

  // Aktiver View-Key: reines Frontend-Signal für die Segmented-Control-
  // Hervorhebung. Auf `/app` = "customer" (unabhängig vom Team-Sub-Modus),
  // auf `/team` = effectiveRole (admin oder concierge).
  const activeView = isCustomer ? "customer" : effectiveRole;

  const performSwitch = useCallback((key) => {
    if (key === "customer") {
      // Für echte Team-Nutzer (Admin/Concierge) öffnen wir den Kunden-
      // Picker-Popup — sie sollen einen konkreten Kunden auswählen, statt
      // in ein leeres /app zu landen. Der gewählte Kunde erscheint als
      // Impersonation in einem NEUEN TAB (2-min-JWT, im
      // Kundenansicht-Audit protokolliert). Der aktuelle Tab behält
      // die Admin-Session.
      // Für echte Kunden/Familie: einfach zu /app navigieren (das ist
      // ihre eigene App-Session).
      if (user?.role === "admin" || user?.role === "concierge") {
        setCustomerPickerOpen(true);
      } else {
        nav("/app");
      }
      return;
    }
    if (key === "admin") {
      // Beim Wechsel zurück zu Admin: Concierge-Impersonation löschen,
      // damit die Datenfilter verschwinden.
      setImpersonatedConcierge(null);
      setEffectiveRole("admin");
      nav("/team");
      return;
    }
    if (key === "concierge") {
      setEffectiveRole("concierge");
      // Ein echter Concierge wird intern auf seine eigene id gefiltert
      // (siehe `activeConciergeId` in lib/auth.js) — kein Picker nötig.
      if (user?.role === "concierge") {
        nav("/team");
        return;
      }
      // Admin muss den konkreten Concierge auswählen. Bereits ein Concierge
      // eingesetzt? Dann direkt in /team, sonst zum Picker.
      if (impersonatedConcierge?.id) {
        nav("/team");
      } else {
        nav("/team/concierge-select");
      }
    }
  }, [nav, setEffectiveRole, setImpersonatedConcierge, user?.role, impersonatedConcierge?.id, setCustomerPickerOpen]);

  // Feb 2026 · User-Directive-Korrektur: „Der PIN wird NICHT beim
  // Rollenwechsel abgefragt. Der PIN wird nur abgefragt, wenn der Admin
  // in der To-Do-Liste oder Aufgaben eines anderen Concierge eine
  // Änderung vornehmen möchte. Der Rollen-Switcher funktioniert ohne PIN."
  // Der Switcher ruft daher direkt `performSwitch` auf; das PIN-Gate lebt
  // ausschließlich im axios-Interceptor + `ImpersonationBanner`.
  const switchView = useCallback((key) => {
    if (!key) return;
    performSwitch(key);
  }, [performSwitch]);

  const canSwitch = viewOptions.length > 1;
  const doLogout = async () => { await logout(); nav("/login"); };

  // Kollabierbare Team-Sidebar (Desktop). Zustand liegt serverseitig pro
  // Nutzer in users.ui_prefs — kein Browser-übergreifender Konflikt mehr.
  const sidebarCollapsed = !!uiPrefs?.sidebar_collapsed;
  const setSidebarCollapsed = (v) =>
    setUiPrefs({ sidebar_collapsed: typeof v === "function" ? !!v(sidebarCollapsed) : !!v });
  const sidebarWidth = sidebarCollapsed ? 64 : 220;

  // Feb 2026 · Unread-Message-Zähler (rote Flaggen systemweit). Polling
  // erfolgt im Hook je nach Rolle über /me/notifications bzw. /threads.
  const unreadRole = isCustomer ? "customer" : isTeam ? "team" : null;
  // 20 Jun 2026 · Concierge sieht nur Nachrichten der ihm zugewiesenen
  // Objekte/Kunden → Badge & Thread-Liste scopen. Admin-Impersonation eines
  // Concierges übergibt dieselbe Scope-ID an /threads.
  const _sidebarScopeConciergeId = isImpersonatingConcierge
    ? (activeConciergeId || impersonatedConcierge?.id || null)
    : null;
  const { total: unreadMessages } = useUnreadMessages(unreadRole, _sidebarScopeConciergeId);

  // Feb 2026 · Sidebar-Zähler-Chips — rote Ziffer neben „Anfragen" und
  // „Objekte", sobald mindestens ein Punkt auf Handlungsbedarf steht.
  // Nur für Team/Admin/Concierge aktiv — Kunden/Partner sehen die Chips nicht.
  //
  // Feb 2026 · 16 Sep 2026 · User-Directive „Admin-Impersonation zeigt nur
  // zugewiesene Daten": Wenn der Admin einen Concierge impersoniert, geben
  // wir die Concierge-ID an den Zähler-Endpoint, damit die Sidebar-Chips
  // exakt die Zahlen zeigen, die der impersonierte Concierge in seiner
  // Eigen-Ansicht auch sähe.
  const { counters: attention } = useAttentionCounters(isTeam, _sidebarScopeConciergeId);

  // Feb 2026 · User-Directive „Admin-Badge auf Angebote-Nav" (3 Sep 2026).
  // Zählt pro eingeloggtem Team-User die ungelesenen Angebote — neue und
  // solche mit vom Kunden geänderten Status. Bei Kunden/Family/Partner
  // bleibt der Hook inaktiv (0).
  const { total: unreadTeamQuotes } = useUnreadTeamQuotes(isTeam);

  // Feb 2026 · User-Directive „Glocken-Badge zeigt nur ungelesene
  // Wochenberichte" (5 Sep 2026). Der Bell-Badge im Kunden-Header
  // zaehlt jetzt nur die ungelesenen Berichte (statt der Summe aller
  // Kategorien). Bumped instant sobald der Kunde einen Bericht oeffnet.
  const { total: notifCount } = useCustomerNotifications(isCustomer);

  // Feb 2026 · Sticky-Header shrink-on-scroll (mobile-friendly). Sobald der
  // Anwender > 32 px nach unten scrollt, wird der Balken auf dem Handy
  // dezent kompakter (halbes Padding) — Desktop bleibt in Voll-Höhe.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const s = (window.scrollY || document.documentElement.scrollTop || 0) > 32;
      setScrolled((cur) => (cur !== s ? s : cur));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Feb 2026 · Publish sticky-header height as CSS var `--wb-header-h` so
  // filter-Balken (Kalender, Anfragen, Dokumente, Aufgaben) systemweit
  // per `sticky top-[var(--wb-header-h)]` direkt unter dem Header andocken.
  // ResizeObserver reagiert automatisch auf den Mobile-Shrink beim Scrollen.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--wb-header-h", `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrolled, isCustomer]);

  // Feb 2026 · Concierge-Übergabe-Toast (User-Directive 1 Sep 2026).
  // Für alle Team-User (Admin sieht bei Selbst-Zuweisung nichts, weil
  // `create_handover` das filtert). Klick auf „Ansehen" springt in die
  // Buchungsliste mit `?open=<booking_id>` — der Deep-Link in
  // TeamBookings öffnet dann direkt das Detail-Modal.
  useHandoverPolling({
    enabled: isTeam && !!user?.id,
    onOpenBooking: (bookingId) => nav(`/team/bookings?open=${bookingId}`),
  });

  // Feb 2026 · User-Directive (3 Sep 2026) „Team-Notification im UI":
  // Toast, sobald ein Kunde ein Angebot genehmigt/ablehnt. Aktiv fuer
  // alle Team-User (Ersteller-basiertes Routing filtert im Backend).
  useQuoteDecisionPolling({
    enabled: isTeam && !!user?.id,
    onOpenQuotes: () => nav("/team/quotes"),
  });

  return (
    <div className="min-h-screen flex flex-col w-full max-w-full" style={{ overflowX: "clip" }}>
      {/* Navy header — Feb 2026: `sticky top-0` damit der Übersichtsbalken
          beim Scrollen dauerhaft sichtbar bleibt. Systemweit — greift für
          Kunden-Ansicht (`/app`), Team (`/team`) und Partner (`/partner`),
          da dieser Layout-Component alle drei Bereiche umhüllt. z-40 hebt
          ihn über modale Inhalts-Elemente (Modals selbst nutzen z-50).
          `data-scrolled` ist der Trigger fürs Mobile-Shrink-Verhalten. */}
      <header
        ref={headerRef}
        data-scrolled={scrolled ? "true" : "false"}
        className="bg-navy text-ivory fixed top-0 left-0 right-0 z-40 transition-shadow"
        style={{
          background: BRAND_COLORS.navy,
          boxShadow: scrolled
            ? `0 1px 0 ${withAlpha(BRAND_COLORS.navy, 0.55)}, 0 6px 20px ${withAlpha(BRAND_COLORS.navy, 0.18)}`
            : `0 1px 0 ${withAlpha(BRAND_COLORS.navy, 0.4)}, 0 6px 20px ${withAlpha(BRAND_COLORS.navy, 0.12)}`,
        }}
      >
        {/* Feb 2026 · Read-only Impersonations-Banner. Renderiert direkt
            im Header, damit der ResizeObserver die Gesamt-Höhe (Banner +
            Balken) automatisch in `--wb-header-h` einrechnet und der
            Main-Content darunter niemals überlagert wird. */}
        <ImpersonationBanner />
        <div
          className={`max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between gap-4 transition-[padding] duration-200 ${
            scrolled ? "py-2 md:py-5" : "py-4 md:py-5"
          }`}
        >
          <Brand to={isPartner ? "/partner" : isTeam ? "/team" : "/app"} />
          {/* Desktop-Nav — Kunden-Links wandern jetzt in eine zweite
              Reihe unter dem Primär-Balken (siehe unten), damit der
              Rechts-Cluster (View-Switcher · DE/EN · Logout) niemals
              vom langen Menü verdrängt wird. Team-Nav lebt in der
              Sidebar. */}
          {/* Right cluster: bell + DE/EN + (mobile: burger) */}
          <div className="flex items-center gap-3 md:gap-4 shrink-0">
            {isCustomer && (
              <button
                data-testid="header-notifications-btn"
                onClick={() => nav("/app/updates")}
                className="relative text-ivory/70 hover:text-ivory p-1"
                aria-label="Benachrichtigungen"
              >
                <Bell className="w-5 h-5" strokeWidth={1.6} />
                {notifCount > 0 && (
                  <span
                    data-testid="header-notifications-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium flex items-center justify-center leading-none shadow"
                    style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                  >
                    {notifCount > 99 ? "99+" : notifCount}
                  </span>
                )}
              </button>
            )}
            {isCustomer && (
              <div className="flex items-center gap-1 rounded-full border border-ivory/25 px-2 py-1 text-[11px] tracking-[0.15em]">
                <button data-testid="lang-de" onClick={() => setLang("de")}
                  className={`px-1.5 ${lang === "de" ? "text-gold" : "text-ivory/60"}`}>DE</button>
                <span className="text-ivory/30">/</span>
                <button data-testid="lang-en" onClick={() => setLang("en")}
                  className={`px-1.5 ${lang === "en" ? "text-gold" : "text-ivory/60"}`}>EN</button>
              </div>
            )}
            {canSwitch && (
              <div className="flex flex-col items-end gap-1">
                <div
                  role="group"
                  aria-label="Ansicht wechseln"
                  data-testid="header-view-switcher"
                  className="inline-flex items-stretch rounded-full border border-ivory/25 overflow-hidden text-[10px] md:text-[11px] tracking-[0.12em] md:tracking-[0.15em] uppercase"
                >
                  {viewOptions.map((opt) => {
                    const active = opt.key === activeView;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => switchView(opt.key)}
                        data-testid={`header-view-switcher-${opt.key}`}
                        aria-pressed={active}
                        className={`px-2 md:px-3 py-1 md:py-1.5 transition-colors ${
                          active
                            ? "text-navy"
                            : "text-ivory/70 hover:text-ivory"
                        }`}
                        style={active ? { background: BRAND_COLORS.gold } : undefined}
                      >
                        <span className="hidden sm:inline">{opt.label}</span>
                        <span className="sm:hidden">{opt.short || opt.label}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Impersonations-Hinweis unter dem Switcher — nur wenn ein
                    Admin gerade als konkreter Concierge unterwegs ist. */}
                {effectiveRole === "concierge" && user?.role === "admin" && activeConciergeName && (
                  <div
                    className="hidden md:flex text-[10.5px] tracking-[0.14em] uppercase text-gold/90 items-center gap-2"
                    data-testid="header-concierge-impersonation-hint"
                  >
                    <span>Als {activeConciergeName}</span>
                    <NavLink
                      to="/team/concierge-select"
                      className="underline decoration-dotted decoration-gold/50 hover:decoration-gold underline-offset-2"
                      data-testid="header-concierge-impersonation-change"
                    >
                      wechseln
                    </NavLink>
                  </div>
                )}
              </div>
            )}
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="hidden md:inline-flex text-[11px] tracking-[0.2em] uppercase text-ivory/70 hover:text-ivory transition-colors"
            >
              {isCustomer ? t.nav.logout : "Abmelden"}
            </button>
            {/* Mobile: hamburger opens More sheet */}
            <button
              data-testid="header-more-btn"
              onClick={() => setMoreOpen(true)}
              className="md:hidden text-ivory/80 p-1"
              aria-label="Menu"
            >
              <MoreHorizontal className="w-6 h-6" strokeWidth={1.6} />
            </button>
          </div>
        </div>
        {/* Feb 2026 · Kunden-Nav in zweiter Reihe (Desktop). Vorher lag
            die 14-Item-Kunden-Nav im Primär-Balken zwischen Brand und
            Rechts-Cluster — das drückte den View-Switcher komplett vom
            Bildschirm. Jetzt: primäre Reihe ist Brand+Switcher+Logout;
            die Kunden-Links leben als scrollbare zweite Reihe (like
            Team-Mobile-Nav). Damit ist der Switcher IMMER sichtbar,
            auch bei Kunden-Ansicht — und der Admin kann sofort zurück
            zu Admin oder Concierge. */}
        {isCustomer && (
            <CustomerNavRow links={links} unreadMessages={unreadMessages} custMoreOpen={custMoreOpen} setCustMoreOpen={setCustMoreOpen} lang={lang} scrolled={scrolled} />
        )}
        {/* Legacy horizontal scroll nav for team/partner mobile only (customers use bottom nav).
            Beim Scrollen wird die mobile Team-Nav-Zeile kompakter (halbes vertikales Padding)
            damit sie weniger Vertikal-Platz frisst. */}
        {!isCustomer && (
            <TeamMobileNav links={links} unreadMessages={unreadMessages} unreadTeamQuotes={unreadTeamQuotes} attention={attention} scrolled={scrolled} />
        )}
      </header>

      {/* Feb 2026 · Team-Sidebar links (Desktop ≥ md). Bleibt fix unter dem
          Header, damit alle Menüpunkte beim Scrollen dauerhaft griffbereit
          sind. Auf Mobile bleibt die horizontale Scroll-Nav unter dem Header
          erhalten (siehe unten). Kollabiert auf 64 px Icon-only via Toggle. */}
      {isTeam && (
        <TeamSidebar links={links} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} sidebarWidth={sidebarWidth} unreadMessages={unreadMessages} unreadTeamQuotes={unreadTeamQuotes} attention={attention} />
      )}

      <main
        className={`flex-1 page-fade ${isTeam ? "transition-[padding-left] duration-200" : ""}`}
        style={{
          paddingTop: "var(--wb-header-h, 96px)",
          paddingLeft: isTeam ? undefined : undefined,
          ...(isTeam ? { "--wb-sidebar-w": `${sidebarWidth}px` } : {}),
        }}
      >
        <OfflineBanner />
        <SessionExpiryBanner />
        <div className={isTeam ? "md:pl-[var(--wb-sidebar-w)]" : ""}>
          <Outlet />
        </div>
      </main>

      <footer
        className={`border-t border-navy/10 mt-16 py-8 text-center pb-24 md:pb-8 ${isTeam ? "transition-[padding-left] duration-200" : ""}`}
        style={isTeam ? { paddingLeft: `var(--wb-sidebar-w, ${sidebarWidth}px)` } : undefined}
      >
        <div className="label-caps" data-testid="footer-tagline">
          {!isTeam && lang !== "en" ? "Weissblom · Exklusive Immobilienbetreuung" : "Weissblom · Private Property Concierge"}
        </div>
        <div className="mt-3 flex items-center justify-center gap-4 flex-wrap px-4">
          <Link to="/impressum" data-testid="footer-impressum-link"
            className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
            {lang === "en" ? "Imprint" : "Impressum"}
          </Link>
          <span className="text-navy/20">·</span>
          <Link to="/datenschutz" data-testid="footer-datenschutz-link"
            className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
            {lang === "en" ? "Privacy" : "Datenschutz"}
          </Link>
        </div>
      </footer>

      {isCustomer && <CustomerBottomNav onOpenMore={() => setMoreOpen(true)} unreadMessages={unreadMessages} newsCount={notifCount} />}
      <InstallPrompt />
      <CustomerPickerModal open={customerPickerOpen} onClose={() => setCustomerPickerOpen(false)} />
      <PinUnlockDialog />
      <MobileMoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        links={links}
        onLogout={doLogout}
        viewOptions={viewOptions}
        activeView={activeView}
        onSwitchView={switchView}
      />
    </div>
  );
}
